import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "styled-components";
import { routeList } from "../../App";
import { lang } from "../../assets/languages/en.language";
import PasswordInput from "../../components/form/Password.input";
import Header from "../../components/Header.component";
import Popup from "../../components/Popup.component";
import { LockResetIcon } from "../../components/svgs/icons/LockReset.icon";
import { usePwaViewportWidth } from "../../hooks/useScreenWidth";
import api from "../../services/api";
import { getVpFontSize } from "../../services/utils";
import { TertiaryFitContentButton } from "../../styles/Button.style";
import { RootContainer } from "../../styles/Container.style";
import { ErrorMessage, FormTitle, StyledForm } from "../../styles/Form";
import { Column, Gap } from "../../styles/Utils";
import { ImageContainer } from "./Landing.page";

interface FormData {
  newPassword: string;
  confirmPassword: string;
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("token"); // ✅ Get token from URL
  const viewportWidth = usePwaViewportWidth();
  const [successPopupVisible, setSuccessPopupVisible] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, errors },
  } = useForm<FormData>({ mode: "onChange" });

  const newPassword = watch("newPassword");

  const onSubmit = async (data: FormData) => {
    try {
      if (!resetToken) throw "Password Reset Token Missing";
      await api.resetPassword(data, resetToken);
      setSuccessPopupVisible(true);
      // mixpanel.track("Log In");
    } catch (err) {
      console.log("Error resetting password", err);
      alert("Error resetting password");
    }
  };

  const goToLanding = () => navigate(routeList.landing);

  return (
    <RootContainer $background={theme.color.primary} $topSafe={true}>
      {successPopupVisible && (
        <Popup
          type="success"
          title={lang.resetPasswordPage.successPopup.title}
          message={lang.resetPasswordPage.successPopup.message}
          buttonText={lang.resetPasswordPage.successPopup.buttonText}
          Icon={LockResetIcon}
          onClose={goToLanding}
          action={goToLanding}
        />
      )}
      <Column $alignItem="center" $maxHeight>
        <Header sloganImgSrc="/slogans/slogan-white.png" />
        <ImageContainer>
          <img src="/illustrations/master-dog-dance.png" />
        </ImageContainer>{" "}
        <Gap $height={2} />
        <FormTitle $fontSize={getVpFontSize(viewportWidth, 8)}>{lang.resetPasswordPage.form.title}</FormTitle>
        <Gap $height={1} />
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <PasswordInput register={register} name="newPassword" />
          {errors.newPassword && <ErrorMessage>{errors.newPassword.message}</ErrorMessage>}
          <Gap $height={1} />
          <PasswordInput
            register={register}
            name="confirmPassword"
            validate={(value) => value === newPassword || lang.resetPasswordPage.form.password.errors.mismatch}
          />
          {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>}
          <Gap $height={1.5} />
          <TertiaryFitContentButton type="submit" disabled={!isValid}>
            <span>{lang.buttons.next}</span>
          </TertiaryFitContentButton>
        </StyledForm>
      </Column>
    </RootContainer>
  );
}
